import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import Boton from '../components/Boton'
import { inicioSegunPantalla } from '../utils/inicio'

// Formato de un código de rescate (XXXXX-XXXXX). El de la app son 6 dígitos.
const ES_RESCATE = /^[A-Za-z0-9]{5}-[A-Za-z0-9]{5}$/

export default function LoginPage() {
  const { login, login2fa } = useAuth()
  const navigate = useNavigate()
  const [form, setForm] = useState({ email: '', password: '' })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // Segundo paso (segundo factor): el desafío que devolvió /login.
  const [desafio, setDesafio] = useState(null)
  const [codigo, setCodigo] = useState('')
  // Marcado por defecto (Martin, 26 sep 2026): en el celular agregado a la
  // pantalla de inicio el código volvía a pedirse en cada entrada.
  const [confiar, setConfiar] = useState(true)
  const [modoRescate, setModoRescate] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const r = await login(form.email, form.password)
      if (r?.requiere_2fa) {
        setDesafio(r.desafio)
        setCodigo('')
        // Marcado por defecto (26 sep): acá se volvía a desmarcar.
        setConfiar(true)
        setModoRescate(false)
        return
      }
      navigate(inicioSegunPantalla())
    } catch (err) {
      setError(err.response?.data?.message || 'Credenciales incorrectas')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit2fa = async (e) => {
    e.preventDefault()
    setError('')
    const limpio = codigo.trim()
    if (!modoRescate && !/^\d{6}$/.test(limpio) && !ES_RESCATE.test(limpio)) {
      setError('Ingresá los 6 dígitos de la app.')
      return
    }
    setLoading(true)
    try {
      const data = await login2fa(desafio, limpio, confiar)
      // Entró con un código de rescate y le quedan pocos: se lo decimos en Seguridad.
      if (data.aviso) navigate('/seguridad', { state: { aviso: data.aviso } })
      else navigate(inicioSegunPantalla())
    } catch (err) {
      const errs = err.response?.data?.errors
      if (errs?.desafio) {
        // Venció o murió por intentos: vuelve al primer paso.
        volver(errs.desafio.join(' '))
        return
      }
      setError(errs ? Object.values(errs).flat().join(' ') : (err.response?.data?.message || 'No se pudo validar el código'))
      setCodigo('')
    } finally {
      setLoading(false)
    }
  }

  const volver = (mensaje = '') => {
    setDesafio(null)
    setCodigo('')
    setError(mensaje)
    setForm(f => ({ ...f, password: '' }))
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>LGR</h1>
        <p style={styles.subtitle}>Sistema de Gestión Comercial y Logística</p>

        {!desafio ? (
          <form onSubmit={handleSubmit} style={styles.form}>
            <label style={styles.label}>Email</label>
            <input
              style={styles.input}
              type="email"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="admin@lgr.local"
              required
            />
            <label style={styles.label}>Contraseña</label>
            <input
              style={styles.input}
              type="password"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder="••••••••"
              required
            />
            {error && <p style={styles.error}>{error}</p>}
            <Boton variante="primary" style={styles.button} type="submit" disabled={loading}>
              {loading ? 'Ingresando...' : 'Ingresar'}
            </Boton>
          </form>
        ) : (
          <form onSubmit={handleSubmit2fa} style={styles.form}>
            <p style={styles.paso}>
              {modoRescate
                ? 'Ingresá uno de tus códigos de rescate. Se usa una sola vez.'
                : 'Ingresá el código de 6 dígitos de tu app de autenticación.'}
            </p>
            <label style={styles.label}>{modoRescate ? 'Código de rescate' : 'Código'}</label>
            <input
              style={{ ...styles.input, ...styles.codigo }}
              type="text"
              value={codigo}
              onChange={e => setCodigo(modoRescate ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder={modoRescate ? 'XXXXX-XXXXX' : '123456'}
              inputMode={modoRescate ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              maxLength={modoRescate ? 11 : 6}
              autoFocus
              required
            />
            <label style={styles.checkRow}>
              <input type="checkbox" checked={confiar} onChange={e => setConfiar(e.target.checked)} />
              Confiar en este dispositivo por 30 días
            </label>
            {error && <p style={styles.error}>{error}</p>}
            <Boton variante="primary" style={styles.button} type="submit" disabled={loading || !codigo}>
              {loading ? 'Verificando...' : 'Entrar'}
            </Boton>
            <div style={styles.links}>
              <button type="button" style={styles.link} onClick={() => { setModoRescate(m => !m); setCodigo(''); setError('') }}>
                {modoRescate ? 'Usar el código de la app' : 'Usar un código de rescate'}
              </button>
              <button type="button" style={styles.link} onClick={() => volver()}>Volver</button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

const styles = {
  container: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6' },
  card: { background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', width: '100%', maxWidth: '400px' },
  title: { margin: '0 0 0.25rem', fontSize: '2rem', color: '#1e293b' },
  subtitle: { margin: '0 0 1.5rem', color: '#64748b', fontSize: '0.875rem' },
  form: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  label: { fontWeight: '600', fontSize: '0.875rem', color: '#374151' },
  input: { padding: '0.625rem 0.875rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '1rem', outline: 'none' },
  codigo: { fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace', fontSize: '1.4rem', letterSpacing: '0.2em', textAlign: 'center' },
  paso: { margin: 0, fontSize: '0.875rem', color: '#475569', lineHeight: 1.45 },
  checkRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151', cursor: 'pointer' },
  error: { color: '#dc2626', fontSize: '0.875rem', margin: 0 },
  button: { padding: '0.75rem', fontSize: '1rem', width: '100%' },
  links: { display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' },
  link: { background: 'none', border: 'none', padding: 0, color: '#1d4ed8', fontSize: '0.8125rem', cursor: 'pointer', textDecoration: 'underline' },
}
